import Parse from "parse"
import { isEqual } from "lodash"

// Recipe paths saved by the "Général" tab, mapped to the label shown on screen
export const GENERAL_FIELD_LABELS = {
    isActive: "Actif",
    legalName: "Dénomination légale",
    name: "Nom technique",
    brands: "Marques",
    enterprises: "Marques",
    status: "Status",
    type: "Type",
    componentType: "Typologie des plats du jour",
    spicy: "Épicé",
    maxProd: "Production maximale",
    ean: "EAN",
    chef: "Chef",
    internalTag: "Tags"
}

// Recipe paths saved by the "Détails" tab, mapped to the label of their row on screen
export const DETAILS_FIELD_LABELS = {
    "defaultValues.appImage": "Photo vue client",
    appImage: "Photo vue client",
    "defaultValues.commercialName": "Nom commercial",
    commercialName: "Nom commercial",
    commercialNames: "Nom commercial",
    "defaultValues.price": "Prix de vente",
    price: "Prix de vente",
    "defaultValues.tva": "TVA",
    tva: "TVA",
    "defaultValues.dlc": "Date limite de consommation (en jours)",
    dlc: "Date limite de consommation (en jours)",
    "defaultValues.description": "Description",
    description: "Description",
    kitchenImage: "Photo barquette",
    "defaultValues.instructions": "Instruction de dressage",
    instructions: "Instruction de dressage",
    "defaultValues.packagingNumber": "Nombre par barquette",
    "defaultValues.packagingNumberUnit": "Nombre par barquette",
    packagingNumber: "Nombre par barquette",
    packagingNumberUnit: "Nombre par barquette",
    gesters: "Nombre de gestes",
    portionPerPlate: "Portion / Plaque",
    difficulty: "Difficulté de production",
    packaging: "Packaging",
    subPackaging: "Sous-Packaging",
    reusablePackaging: "Packaging réutilisable",
    reusableSubPackaging: "Sous-Packaging réutilisable",
    preparation: "Préparation",
    specialInstruction: "Instruction spécifique",
    heatingInstructions: "Chauffage"
}

export const NEWS_FIELD_LABEL = "Remarques et actualité"

export const SWITCH_PRODUCTION_STEPS_LABEL = "Données utilisées pour cette fiche technique"

const compareNormalized = (a, b) => {
    const left = JSON.stringify(a)
    const right = JSON.stringify(b)
    if (left === right) return 0
    return left < right ? -1 : 1
}

/**
 * Reduces a value to a plain, order-independent shape so that two saves of the same content compare equal:
 * empty values (undefined, null, "", []) all become null, Parse objects and pointers become "ClassName:objectId",
 * and arrays are sorted (no field of these tabs carries a meaningful order).
 */
export const normalizeForDiff = (value) => {
    if (value === undefined || value === null || value === "") return null
    if (value instanceof Parse.Object) return `${value.className}:${value.id}`
    if (value instanceof Date) return value.toISOString()
    if (Array.isArray(value)) {
        const items = value.map(normalizeForDiff).filter(item => item !== null)
        return items.length ? items.sort(compareNormalized) : null
    }
    if (typeof value === "object") {
        if (value.objectId && value.className) return `${value.className}:${value.objectId}`
        const entries = Object.keys(value)
            .sort()
            .map(key => [key, normalizeForDiff(value[key])])
            .filter(([, normalized]) => normalized !== null)
        return entries.length ? Object.fromEntries(entries) : null
    }
    return value
}

const getPathValue = (recipe, path) => {
    const [field, ...subPath] = path.split(".")
    return subPath.reduce((value, key) => (value ? value[key] : undefined), recipe.get(field))
}

/**
 * Copies the current value of each labelled path, to compare it after the form values are set on the recipe.
 * Normalizing makes a deep copy, so fields mutated in place (defaultValues) do not alter the snapshot.
 */
export const snapshotFields = (recipe, fieldLabels) => {
    return Object.keys(fieldLabels).reduce((snapshot, path) => {
        snapshot[path] = normalizeForDiff(getPathValue(recipe, path))
        return snapshot
    }, {})
}

export const getChangedFieldLabels = (snapshot, recipe, fieldLabels) => {
    const labels = Object.keys(fieldLabels)
        .filter(path => !isEqual(snapshot[path], normalizeForDiff(getPathValue(recipe, path))))
        .map(path => fieldLabels[path])

    return [...new Set(labels)]
}

/**
 * Labels of the sections a "Recette" or "Étapes de prod" save actually wrote:
 * sections rewritten or created (collected by parseSectionToParse) and sections removed from the recipe.
 */
export const getChangedSectionLabels = ({ writtenSections = [], previousSections = [], nextSectionIds = [] }) => {
    const written = writtenSections.map(({ name, isNew }) => isNew ? `Section ${name} (ajoutée)` : `Section ${name}`)
    const removed = previousSections
        .filter(section => !nextSectionIds.includes(section.id))
        .map(section => `Section ${section.get("name")} (supprimée)`)

    return [...new Set([...written, ...removed])]
}
